import { useCallback, useEffect, useRef } from "react";

type UseIntersectionObserverOptions = {
  enabled?: boolean;
  options?: IntersectionObserverInit;
};
/**
 * Custom hook to observe the visibility of a DOM element using IntersectionObserver.
 *
 * @param {function} onIntersect - Callback fired when the element enters or leaves the viewport.
 *        Receives two arguments:
 *        1. `isIntersect` (boolean) - true if the element is intersecting, false otherwise.
 *        2. `intersectionObj` (IntersectionObserverEntry | undefined) - the IntersectionObserver entry object.
 *
 * @param {Object} [option] - Optional settings for the hook.
 * @param {boolean} [option.enabled=true] - If false, the observer will not run.
 * @param {IntersectionObserverInit} [option.options] - IntersectionObserver options like `root`, `rootMargin`, and `threshold`.
 *
 * @returns {Object} Returns an object containing:
 * @returns {function} intersectElementRef - A ref callback that should be attached to the DOM element to observe.
 *
 * @example
 * ```tsx
 * import React from "react";
 * import { useIntersectionObserver } from "./useIntersectionObserver";
 *
 * const ExampleComponent = () => {
 *   const { intersectElementRef } = useIntersectionObserver((isVisible, entry) => {
 *     if (isVisible) {
 *       console.log("Element is visible in viewport!", entry);
 *     } else {
 *       console.log("Element is no longer visible.");
 *     }
 *   }, {
 *     enabled: true,
 *     options: { root: null, rootMargin: "0px", threshold: 0.5 }
 *   });
 *
 *   return (
 *     <Box sx={{ height: "200vh" }}>
 *       <Box
 *         ref={intersectElementRef}
 *         sx={{ marginTop: "100vh", height: "100px", background: "rebeccapurple" }}
 *       >
 *         Observe me
 *       </Box>
 *     </Box>
 *   );
 * };
 * ```
 */
export default function useIntersectionObserver(
  onIntersect: (isIntersect: boolean, intersectionObj?: IntersectionObserverEntry) => void,
  option?: UseIntersectionObserverOptions
) {
  const { enabled = true, options } = option || {};
  const observerRef = useRef<IntersectionObserver>();
  const elementRef = useRef<HTMLElement | null>(null);
  const callbackRef = useRef(onIntersect);

  callbackRef.current = onIntersect;

  useEffect(() => {
    if (!enabled) {
      // Clean up observer if disabled
      observerRef.current?.disconnect();
      observerRef.current = undefined;
      return;
    }

    if (!window?.IntersectionObserver) {
      console.warn("Your browser doesn't support IntersectionObserver feature.");
      return;
    }

    // Create new observer
    observerRef.current = new IntersectionObserver((entries) => {
      const entry = entries?.[0];
      if (callbackRef.current) {
        callbackRef.current(entry.isIntersecting, entry);
      }
    }, options || {});

    // If we have a stored element, observe it now
    if (elementRef.current) {
      observerRef.current.observe(elementRef.current);
    }

    return () => {
      observerRef.current?.disconnect();
      observerRef.current = undefined;
    };
  }, [enabled, JSON.stringify(options)]);

  const intersectElementRef = useCallback(
    (element: HTMLElement | null) => {
      // Store the element reference
      elementRef.current = element;

      if (!enabled) return;

      // Access observerRef.current directly
      const observer = observerRef.current;

      // Disconnect previous observations
      if (observer) {
        observer.disconnect();

        // Observe new element if it exists
        if (element) {
          observer.observe(element);
        }
      }
    },
    [enabled]
  );

  return { intersectElementRef } as const;
}
